import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { GetCustomPlaceholdersRPC } from "@pulse/pulse-rpcs/lib/email-cycle/getCustomPlaceholdersRPC";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { getAPIClient } from "../../networking/APIClient";
import { useGetCustomPlaceholdersRPCClientImpl } from "../rpcs/RPC";
import {
  createCustomPlaceholderModel,
  CustomPlaceholderModel,
} from "../models/CustomPlaceholderModel";

const getInitialState = (): SnapshotIn<typeof CustomPlaceholdersStore> => ({
  customPlaceholders: [],
  projectId: null,
  hasFetchedCustomPlaceholders: false,
  loadingCustomPlaceholders: false,
  getCustomPlaceholdersRPCError: null,
  hasUnexpectedGetCustomPlaceholdersError: false,
});

export const CustomPlaceholdersStore = types
  .model("CustomPlaceholdersStore", {
    customPlaceholders: types.array(CustomPlaceholderModel),
    projectId: types.maybeNull(types.string),
    hasFetchedCustomPlaceholders: types.boolean,
    loadingCustomPlaceholders: types.boolean,
    getCustomPlaceholdersRPCError: types.maybeNull(
      types.enumeration(
        "GetCustomPlaceholdersRPCError",
        Object.values(GetCustomPlaceholdersRPC.RPCError),
      ),
    ),
    hasUnexpectedGetCustomPlaceholdersError: types.boolean,
  })
  .actions((store) => ({
    setProjectId: (projectId: string | null): void => {
      if (store.projectId === projectId) return;

      store.projectId = projectId;
      store.hasFetchedCustomPlaceholders = false;
      store.customPlaceholders.clear();
      store.getCustomPlaceholdersRPCError = null;
      store.hasUnexpectedGetCustomPlaceholdersError = false;
      store.loadingCustomPlaceholders = false;
    },
    fetchCustomPlaceholders: flow(function* (projectId: string | null) {
      const isSameProject = store.projectId === projectId;
      if (!isSameProject) {
        store.projectId = projectId;
        store.hasFetchedCustomPlaceholders = false;
        store.customPlaceholders.clear();
      }

      if (store.loadingCustomPlaceholders) return;
      if (store.hasFetchedCustomPlaceholders && store.projectId === projectId) {
        return;
      }

      store.loadingCustomPlaceholders = true;
      store.getCustomPlaceholdersRPCError = null;
      store.hasUnexpectedGetCustomPlaceholdersError = false;

      try {
        const apiClient = getAPIClient(store);
        const request = new GetCustomPlaceholdersRPC.Request(
          projectId ? new LeoUUID(projectId) : null,
        );

        const result: LeoRPCResult<
          GetCustomPlaceholdersRPC.Response,
          GetCustomPlaceholdersRPC.Errors.Errors
        > =
          yield useGetCustomPlaceholdersRPCClientImpl(apiClient).execute(
            request,
          );

        if ("response" in result) {
          store.customPlaceholders.replace(
            result.response.customPlaceholders.map((customPlaceholder) =>
              createCustomPlaceholderModel({
                displayName: customPlaceholder.displayName,
                placeholder: customPlaceholder.placeholder,
              }),
            ),
          );
          store.projectId = projectId;
          store.hasFetchedCustomPlaceholders = true;
          return;
        }

        switch (result.error.code) {
          case GetCustomPlaceholdersRPC.RPCError.InvalidProjectId:
          case GetCustomPlaceholdersRPC.RPCError.ProjectAlreadyArchived:
            store.getCustomPlaceholdersRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
            store.hasUnexpectedGetCustomPlaceholdersError = true;
        }
      } catch {
        store.hasUnexpectedGetCustomPlaceholdersError = true;
      } finally {
        store.loadingCustomPlaceholders = false;
      }
    }),
    clearUnexpectedGetCustomPlaceholdersError: (): void => {
      store.hasUnexpectedGetCustomPlaceholdersError = false;
    },
    clearGetCustomPlaceholdersRPCError: (): void => {
      store.getCustomPlaceholdersRPCError = null;
    },
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createCustomPlaceholdersStore = (): Instance<
  typeof CustomPlaceholdersStore
> => CustomPlaceholdersStore.create(getInitialState());
